import {
  ChevronLeft,
  ChevronRight,
  Close as CloseIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  ListItemButton,
  Stack,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { type ReactNode, useState } from "react";
import { useContainerBreakpoint } from "../../hooks/useContainerBreakpoint";
import {
  CONDENSED_MENU_WIDTH,
  DESKTOP_MENU_WIDTH,
  DRAWER_WIDTH,
} from "./constants";
import { MenuCondensedContext } from "./MenuCondensedContext";

interface ResponsiveMenuLayoutProps {
  /**
   * Title to be displayed on light background
   */
  title: ReactNode;
  /**
   * Title to be displayed on dark background
   */
  titleInverted: ReactNode;
  /**
   * Menu component to be displayed on the left side on desktop
   * or in the drawer on mobile
   */
  menu: ReactNode;
  topBar?: ReactNode;
  /**
   * Main content to be displayed
   */
  children: ReactNode;
}

export function ResponsiveMenuLayout({
  title,
  titleInverted,
  menu,
  children,
  topBar,
}: ResponsiveMenuLayoutProps) {
  const theme = useTheme();
  const viewportIsMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { ref: containerRef, isBelow } = useContainerBreakpoint(
    theme.breakpoints.values.md
  );
  // Use actual container width when available, fall back to viewport query
  // for initial render. This ensures the layout responds to the actual available
  // width (e.g. in Chrome split view) rather than the full browser window width.
  const isMobile = isBelow ?? viewportIsMobile;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isCondensed, setIsCondensed] = useState(false);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Mobile layout with AppBar and Drawer
  if (isMobile) {
    return (
      <Box
        ref={containerRef}
        sx={{ display: "flex", flexDirection: "column", height: "100vh" }}
      >
        <AppBar position="static">
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open menu"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            {titleInverted}
          </Toolbar>
        </AppBar>

        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", p: 1 }}>
            <IconButton onClick={handleDrawerToggle}>
              <CloseIcon />
            </IconButton>
            {title}
          </Box>
          {menu}
        </Drawer>

        <Stack
          sx={{
            flexGrow: 1,
            overflow: "auto",
          }}
        >
          {topBar}
          <Box
            sx={{
              pt: 1,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {children}
          </Box>
        </Stack>
      </Box>
    );
  }

  const sidebarWidth = isCondensed ? CONDENSED_MENU_WIDTH : DESKTOP_MENU_WIDTH;

  // Desktop layout with fixed menu on the left
  return (
    <Box ref={containerRef} sx={{ display: "flex", height: "100vh" }}>
      <Box
        component="nav"
        sx={{
          width: sidebarWidth,
          flexShrink: 0,
          borderRight: 1,
          borderColor: "divider",
          overflow: "hidden",
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isCondensed ? "center" : "space-between",
          }}
        >
          {!isCondensed && (
            <ListItemButton sx={{ flexGrow: 1 }}>{title}</ListItemButton>
          )}
          <Tooltip title={isCondensed ? "Menu ausklappen" : "Menu einklappen"}>
            <IconButton
              onClick={() => setIsCondensed((prev) => !prev)}
              aria-label={isCondensed ? "expand menu" : "collapse menu"}
              size="small"
              sx={{ m: 0.5 }}
            >
              {isCondensed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Tooltip>
        </Box>
        <MenuCondensedContext.Provider value={{ isCondensed }}>
          {menu}
        </MenuCondensedContext.Provider>
      </Box>
      <Stack
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        {topBar}
        <Box
          sx={{
            pt: 1,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {children}
        </Box>
      </Stack>
    </Box>
  );
}
